import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import TextInput from '@adsk/alloy-react-text-input';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import SelectBox from '@adsk/alloy-select-box';
import iso3166 from 'iso-3166-2';
import { flexStyle, spacedStyle, linkButtonStyle } from './styles';
import AutocompleteAddress from '../AutocompleteAddress';

const ADDRESS_INPUT_MODES = {
  AUTOCOMPLETE: 'autocomplete',
  MANUAL: 'manual',
};

const AutocompleteOrManualAddress = ({
  address,
  autocompletedAddress,
  autocompleteAddressPlaceholder,
  defaultCountry,
  disabled,
  inputMode,
  isInModal,
  isMapboxTokenActive,
  mapBoxAccessToken,
  onAddressChange,
  onAutocompletedAddressResult,
  onInputModeChange,
  setMapboxTokenActive,
  showManualCountry,
  resetAddressOnDelete,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const {
    addressLine1,
    addressLine2,
    city,
    stateOrProvince,
    postalCode,
    country,
  } = address;
  const linkText =
    inputMode === ADDRESS_INPUT_MODES.MANUAL
      ? T.translate('ADDRESS_AUTOCOMPLETE')
      : T.translate('ADDRESS_MANUAL');
  const countries = useMemo(() => {
    return Object.entries(iso3166.data)
      .map(([key, value]) => ({
        isoCode: key,
        name: value.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);
  const regions = useMemo(() => {
    if (country === '' || country === null || !iso3166.data[country]?.sub)
      return [];

    return Object.entries(iso3166.data[country].sub)
      .map(([key, value]) => ({
        isoCode: key.split('-')[1],
        name: value.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [country]);
  const countryValue = useMemo(() => {
    return country !== '' && country !== null ? { isoCode: country } : null;
  }, [country]);
  const stateOrProvinceValue = useMemo(() => {
    return stateOrProvince !== '' && stateOrProvince != null
      ? { isoCode: stateOrProvince }
      : null;
  }, [stateOrProvince]);

  const handleActivateMapboxToken = () => {
    if (isMapboxTokenActive) return;

    setMapboxTokenActive(true);
    onInputModeChange(ADDRESS_INPUT_MODES.AUTOCOMPLETE);
  };

  const handleFailedActivateMapboxToken = () => {
    setMapboxTokenActive(true);
    onInputModeChange(ADDRESS_INPUT_MODES.MANUAL);
  };

  const handleLinkButtonClick = () => {
    const { MANUAL, AUTOCOMPLETE } = ADDRESS_INPUT_MODES;
    onInputModeChange(inputMode === MANUAL ? AUTOCOMPLETE : MANUAL);
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        {!disabled && isMapboxTokenActive && (
          <LinkButton
            style={linkButtonStyle}
            type="button"
            onClick={handleLinkButtonClick}
          >
            {linkText}
          </LinkButton>
        )}
      </div>
      {inputMode === ADDRESS_INPUT_MODES.AUTOCOMPLETE && (
        <AutocompleteAddress
          autocompletedAddress={autocompletedAddress}
          defaultCountry={defaultCountry}
          disabled={disabled || inputMode === ADDRESS_INPUT_MODES.MANUAL}
          isInModal={isInModal}
          mapBoxAccessToken={mapBoxAccessToken}
          onActivateMapboxToken={handleActivateMapboxToken}
          onAddressChange={onAddressChange}
          onFailedActivateMapboxToken={handleFailedActivateMapboxToken}
          onResult={onAutocompletedAddressResult}
          placeholder={
            autocompleteAddressPlaceholder ||
            T.translate('ADDRESS_AUTOCOMPLETE_PLACEHOLDER')
          }
          resetAddressOnDelete={resetAddressOnDelete}
        />
      )}
      {inputMode === ADDRESS_INPUT_MODES.MANUAL && (
        <>
          {showManualCountry && (
            <SelectBox
              clearable
              disabled={disabled}
              labelKey="name"
              maxMenuHeight={150}
              onChange={(value) =>
                onAddressChange({
                  country: value ? value.isoCode : '',
                  stateOrProvince: '',
                })
              }
              options={countries}
              placeholder={T.translate('ADDRESS_COUNTRY')}
              searchable
              valueKey="isoCode"
              value={countryValue}
            />
          )}
          <TextInput
            disabled={disabled}
            onChange={(e) => onAddressChange({ addressLine1: e.target.value })}
            maxLength="255"
            placeholder={T.translate('ADDRESS_LINE_1')}
            style={showManualCountry ? { marginTop: '15px' } : null}
            type="text"
            value={addressLine1}
          />
          <TextInput
            disabled={disabled}
            onChange={(e) => onAddressChange({ addressLine2: e.target.value })}
            maxLength="255"
            placeholder={T.translate('ADDRESS_LINE_2')}
            style={{ marginTop: '15px' }}
            type="text"
            value={addressLine2}
          />
          <TextInput
            disabled={disabled}
            onChange={(e) => onAddressChange({ city: e.target.value })}
            maxLength="255"
            placeholder={T.translate('ADDRESS_CITY')}
            style={{ marginTop: '15px' }}
            type="text"
            value={city}
          />
          <div style={flexStyle}>
            <div style={spacedStyle}>
              <SelectBox
                clearable
                disabled={disabled || country === ''}
                labelKey="name"
                maxMenuHeight={150}
                onChange={(value) =>
                  onAddressChange({
                    stateOrProvince: value ? value.isoCode : '',
                  })
                }
                options={regions}
                placeholder={T.translate('ADDRESS_STATE_REGION')}
                searchable
                valueKey="isoCode"
                value={stateOrProvinceValue}
              />
            </div>
            <TextInput
              disabled={disabled}
              onChange={(e) => onAddressChange({ postalCode: e.target.value })}
              maxLength="255"
              placeholder={T.translate('ADDRESS_ZIP_CODE')}
              style={spacedStyle}
              type="text"
              value={postalCode}
            />
          </div>
        </>
      )}
    </>
  );
};

AutocompleteOrManualAddress.propTypes = {
  address: PropTypes.shape({
    addressLine1: PropTypes.string.isRequired,
    addressLine2: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    stateOrProvince: PropTypes.string,
  }).isRequired,
  autocompleteAddressPlaceholder: PropTypes.string,
  autocompletedAddress: PropTypes.string.isRequired,
  defaultCountry: PropTypes.string,
  disabled: PropTypes.bool,
  inputMode: PropTypes.string.isRequired,
  isInModal: PropTypes.bool,
  isMapboxTokenActive: PropTypes.bool.isRequired,
  mapBoxAccessToken: PropTypes.string.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onAutocompletedAddressResult: PropTypes.func.isRequired,
  onInputModeChange: PropTypes.func.isRequired,
  resetAddressOnDelete: PropTypes.bool.isRequired,
  setMapboxTokenActive: PropTypes.func.isRequired,
  showManualCountry: PropTypes.bool,
};

AutocompleteOrManualAddress.defaultProps = {
  autocompleteAddressPlaceholder: undefined,
  defaultCountry: '',
  disabled: false,
  isInModal: false,
  showManualCountry: true,
};

AutocompleteOrManualAddress.ADDRESS_INPUT_MODES = ADDRESS_INPUT_MODES;

export default AutocompleteOrManualAddress;
